// Ids arrive as ObjectIds on the API and as strings in the browser.
export type CouponScopeId = string | { toString(): string };

type IdList = readonly CouponScopeId[] | null | undefined;

export const COUPON_APPLIES_TO = [
  'wholeOrder',
  'prices',
  'pricesAndBumps',
  'tickets',
  'ticketsAndAddOns',
] as const;
export type CouponAppliesTo = (typeof COUPON_APPLIES_TO)[number];

export const CHECKOUT_COUPON_APPLIES_TO = ['wholeOrder', 'prices', 'pricesAndBumps'] as const;
export const EVENT_COUPON_APPLIES_TO = ['wholeOrder', 'tickets', 'ticketsAndAddOns'] as const;

/**
 * Where and what a coupon discounts on one page. An empty list means all of them, including those
 * added later: every price of the main product, every ticket of the event, every bump or add-on.
 * `bumps` is read only by pricesAndBumps and ticketsAndAddOns.
 */
export interface CouponRule {
  page?: CouponScopeId;
  /** The API's output shape names the page `pageId`. */
  pageId?: CouponScopeId;
  appliesTo: CouponAppliesTo;
  prices?: IdList;
  tickets?: IdList;
  bumps?: IdList;
}

export interface ResolvedCouponRule {
  page: string;
  appliesTo: CouponAppliesTo;
  prices: string[];
  tickets: string[];
  bumps: string[];
}

/**
 * A coupon or a record of one (charge, subscription, Order). Without rules a coupon has main's
 * shape: `pages` absent is every page, `pages: []` no page, and `ticketTypes` limits an event in
 * `pages` to those tickets.
 */
export interface CouponScope {
  rules?: readonly CouponRule[] | null;
  pages?: IdList;
  ticketTypes?: IdList;
  applied?: boolean | null;
}

export interface CouponAmounts {
  amountOff?: number | null;
  percentOff?: number | null;
}

export interface CouponPriceLine {
  productId: CouponScopeId;
  priceId?: CouponScopeId | null;
}

export type CouponPrimaryScope = 'order' | 'ticket' | 'none';

export interface CouponPurchase {
  pageId: CouponScopeId;
  /** The event's ticket types, which place a legacy coupon's `ticketTypes` on it. */
  pageTicketTypeIds?: IdList;
  priceId?: CouponScopeId | null;
  /** Ticket types bought, at least one ticket each. */
  ticketTypeIds?: IdList;
  bumpProductIds?: IdList;
}

export interface CouponReach {
  /** A rule names the page, or the coupon works everywhere. */
  eligible: boolean;
  /** A rule matched, so the lines it reaches are discounted. */
  applied: boolean;
  rules: ResolvedCouponRule[];
  /** 'order': the main line or every ticket as one; 'ticket': per ticket, the types in `ticketTypes`. */
  primaryScope: CouponPrimaryScope;
  ticketTypes: string[];
  allBumps: boolean;
  bumps: string[];
  /** A ticketsAndAddOns rule matched: an amount off comes off once, shared with the add-ons. */
  ticketsShareWithAddOns: boolean;
}

const toIds = (list: IdList) => (list ?? []).map(String);

const unique = (ids: string[]) => [...new Set(ids)];

const includesId = (list: IdList, id: CouponScopeId | null | undefined) =>
  id != null && toIds(list).includes(String(id));

const hasRules = (coupon: CouponScope) => (coupon.rules?.length ?? 0) > 0;

const rulePage = (rule: CouponRule) => String(rule.page ?? rule.pageId);

const resolveRule = (rule: CouponRule): ResolvedCouponRule => ({
  page: rulePage(rule),
  appliesTo: rule.appliesTo,
  prices: unique(toIds(rule.prices)),
  tickets: unique(toIds(rule.tickets)),
  bumps: unique(toIds(rule.bumps)),
});

const wholeOrderRule = (pageId: CouponScopeId): ResolvedCouponRule => ({
  page: String(pageId),
  appliesTo: 'wholeOrder',
  prices: [],
  tickets: [],
  bumps: [],
});

// A stored `pages: []` matches no page, so it is not global.
export function isGlobalCoupon(coupon: CouponScope): boolean {
  return !hasRules(coupon) && coupon.pages == null;
}

export function getCouponPageIds(coupon: CouponScope | null | undefined): string[] {
  if (!coupon) return [];
  if (hasRules(coupon)) return unique(coupon.rules!.map(rulePage));
  return unique(toIds(coupon.pages));
}

export function isCouponEligibleOnPage(
  coupon: CouponScope,
  { pageId }: { pageId: CouponScopeId },
): boolean {
  return isGlobalCoupon(coupon) || getCouponPageIds(coupon).includes(String(pageId));
}

const legacyPageRule = (
  coupon: CouponScope,
  pageId: CouponScopeId,
  pageTicketTypeIds: IdList,
): ResolvedCouponRule => {
  const tickets = unique(toIds(coupon.ticketTypes)).filter(id => includesId(pageTicketTypeIds, id));
  return tickets.length
    ? { ...wholeOrderRule(pageId), appliesTo: 'tickets', tickets }
    : wholeOrderRule(pageId);
};

/**
 * Every rule of the coupon. A coupon without rules is read as main stored it: an event in `pages`
 * with any of its tickets in `ticketTypes` is a tickets rule, every other page whole order.
 * `ticketTypePage` names the event of a ticket type.
 */
export function getCouponRules(
  coupon: CouponScope | null | undefined,
  { ticketTypePage }: { ticketTypePage?: (ticketTypeId: string) => string | null | undefined } = {},
): ResolvedCouponRule[] {
  if (!coupon) return [];
  if (hasRules(coupon)) return coupon.rules!.map(resolveRule);

  return getCouponPageIds(coupon).map(pageId =>
    legacyPageRule(
      coupon,
      pageId,
      toIds(coupon.ticketTypes).filter(id => String(ticketTypePage?.(id) ?? '') === pageId),
    ),
  );
}

/** The coupon's rules on one page; a global coupon has one whole-order rule everywhere. */
export function getCouponPageRules(
  coupon: CouponScope | null | undefined,
  { pageId, pageTicketTypeIds }: { pageId: CouponScopeId; pageTicketTypeIds?: IdList },
): ResolvedCouponRule[] {
  if (!coupon) return [];
  if (hasRules(coupon)) {
    return coupon.rules!.filter(rule => rulePage(rule) === String(pageId)).map(resolveRule);
  }
  if (coupon.pages == null) return [wholeOrderRule(pageId)];
  if (!includesId(coupon.pages, pageId)) return [];
  return [legacyPageRule(coupon, pageId, pageTicketTypeIds)];
}

const ADD_ON_MODES: readonly CouponAppliesTo[] = ['pricesAndBumps', 'ticketsAndAddOns'];
const MAIN_LINE_MODES: readonly CouponAppliesTo[] = ['wholeOrder', 'prices', 'pricesAndBumps'];
const TICKET_MODES: readonly CouponAppliesTo[] = ['tickets', 'ticketsAndAddOns'];

const boughtBumpsInRule = (rule: ResolvedCouponRule, bumpProductIds: string[]) =>
  bumpProductIds.filter(id => rule.bumps.length === 0 || rule.bumps.includes(id));

const ruleMatches = (
  rule: ResolvedCouponRule,
  {
    priceId,
    ticketTypeIds,
    bumpProductIds,
  }: { priceId: string | null; ticketTypeIds: string[]; bumpProductIds: string[] },
) => {
  const boughtBump = boughtBumpsInRule(rule, bumpProductIds).length > 0;
  const boughtTicket =
    rule.tickets.length === 0
      ? ticketTypeIds.length > 0
      : ticketTypeIds.some(id => rule.tickets.includes(id));
  const pickedPrice =
    rule.prices.length === 0 || (priceId != null && rule.prices.includes(priceId));

  switch (rule.appliesTo) {
    case 'wholeOrder':
      return true;
    case 'prices':
      return pickedPrice;
    case 'pricesAndBumps':
      return pickedPrice && boughtBump;
    case 'tickets':
      return boughtTicket;
    case 'ticketsAndAddOns':
      return boughtTicket && boughtBump;
    default:
      return false;
  }
};

// Records freeze the event's tickets in place of an empty list, so an Order rebuild needs no page.
const withEveryTicket = (rules: ResolvedCouponRule[], ticketIds: string[]) =>
  rules.map(rule =>
    TICKET_MODES.includes(rule.appliesTo) && rule.tickets.length === 0
      ? { ...rule, tickets: ticketIds }
      : rule,
  );

/** What a set of rules that already matched reaches: the union, each line once. */
const reachOfRules = (rules: ResolvedCouponRule[]) => {
  const has = (modes: readonly CouponAppliesTo[]) =>
    rules.some(rule => modes.includes(rule.appliesTo));
  const primaryScope: CouponPrimaryScope = has(MAIN_LINE_MODES)
    ? 'order'
    : has(TICKET_MODES)
      ? 'ticket'
      : 'none';
  const addOnRules = rules.filter(rule => ADD_ON_MODES.includes(rule.appliesTo));

  return {
    primaryScope,
    ticketTypes:
      primaryScope === 'ticket'
        ? unique(
            rules
              .filter(rule => TICKET_MODES.includes(rule.appliesTo))
              .flatMap(rule => rule.tickets),
          )
        : [],
    allBumps: has(['wholeOrder']) || addOnRules.some(rule => rule.bumps.length === 0),
    bumps: unique(addOnRules.flatMap(rule => rule.bumps)),
    ticketsShareWithAddOns: primaryScope === 'ticket' && has(['ticketsAndAddOns']),
  };
};

const NO_REACH: Omit<CouponReach, 'eligible'> = {
  applied: false,
  rules: [],
  primaryScope: 'none',
  ticketTypes: [],
  allBumps: false,
  bumps: [],
  ticketsShareWithAddOns: false,
};

/**
 * Which of the coupon's rules match this purchase and what they reach. The rules of a page are
 * OR'd; a pricesAndBumps or ticketsAndAddOns rule matches only with one of its bumps bought.
 */
export function resolveCouponReach(
  coupon: CouponScope | null | undefined,
  purchase: CouponPurchase,
): CouponReach {
  if (!coupon) return { eligible: false, ...NO_REACH };

  const namedRules = getCouponPageRules(coupon, purchase);
  // findValidCoupon refuses a page the coupon does not name; past it, main billed the whole order.
  const pageRules = namedRules.length ? namedRules : [wholeOrderRule(purchase.pageId)];
  const eligible = namedRules.length > 0;

  const bought = {
    priceId: purchase.priceId != null ? String(purchase.priceId) : null,
    ticketTypeIds: toIds(purchase.ticketTypeIds),
    bumpProductIds: toIds(purchase.bumpProductIds),
  };
  const rules = withEveryTicket(
    pageRules.filter(rule => ruleMatches(rule, bought)),
    unique([...toIds(purchase.pageTicketTypeIds), ...bought.ticketTypeIds]),
  );
  if (rules.length === 0) return { eligible, ...NO_REACH };

  return { eligible, applied: true, rules, ...reachOfRules(rules) };
}

export const couponReachesBump = (
  reach: Pick<CouponReach, 'applied' | 'allBumps' | 'bumps'>,
  productId: CouponScopeId,
): boolean => reach.applied && (reach.allBumps || reach.bumps.includes(String(productId)));

/**
 * The fields a charge, subscription or Order freezes: the rules that matched, so a rebuild never
 * re-reads the coupon. A coupon without stored rules that applied to an order without bumps keeps
 * main's record shape; `applied: false` is still recorded so receipts show $0, not the face value.
 */
export function getCouponRecordFields(
  coupon: CouponScope | null | undefined,
  reach: CouponReach,
  { hasBumps }: { hasBumps: boolean },
): { rules?: ResolvedCouponRule[]; applied?: boolean } {
  if (!(coupon && hasRules(coupon)) && reach.applied && !hasBumps) return {};
  return { rules: reach.rules, applied: reach.applied };
}

export interface RecordedCouponReach {
  applied: boolean;
  primaryScope: CouponPrimaryScope;
  ticketTypes: string[];
  ticketsShareWithAddOns: boolean;
  /** The record froze its rules; one without them reads as main did. */
  hasRules: boolean;
  reachesBump: (productId: CouponScopeId) => boolean;
}

/**
 * What a record says the coupon reached. Frozen rules are read as they matched; a record without
 * them reads as main did: the main line, or the event's tickets it named, and never a bump.
 */
export function getRecordedCouponReach(
  record: CouponScope | null | undefined,
  { pageId, pageTicketTypeIds }: { pageId: CouponScopeId; pageTicketTypeIds?: IdList },
): RecordedCouponReach {
  const none: RecordedCouponReach = {
    applied: false,
    primaryScope: 'none',
    ticketTypes: [],
    ticketsShareWithAddOns: false,
    hasRules: false,
    reachesBump: () => false,
  };
  if (!record) return none;
  if (record.applied === false) return { ...none, hasRules: Array.isArray(record.rules) };

  if (hasRules(record)) {
    const reach = {
      applied: true,
      ...reachOfRules(
        withEveryTicket(record.rules!.map(resolveRule), unique(toIds(pageTicketTypeIds))),
      ),
    };
    return {
      applied: true,
      primaryScope: reach.primaryScope,
      ticketTypes: reach.ticketTypes,
      ticketsShareWithAddOns: reach.ticketsShareWithAddOns,
      hasRules: true,
      reachesBump: productId => couponReachesBump(reach, productId),
    };
  }

  const legacy =
    record.pages != null && includesId(record.pages, pageId)
      ? legacyPageRule(record, pageId, pageTicketTypeIds)
      : wholeOrderRule(pageId);
  return {
    ...none,
    applied: true,
    primaryScope: legacy.appliesTo === 'tickets' ? 'ticket' : 'order',
    ticketTypes: legacy.tickets,
  };
}

/**
 * An amount off comes off once, shared equally. A line's share stops at its own amount and the
 * rest is shared across the others; odd cents go to the first line still open.
 */
export function splitAmountOffEqually(amountOff: number, bases: readonly number[]): number[] {
  const shares = bases.map(() => 0);
  let remaining = Math.max(0, Math.round(amountOff));
  let open = bases.map((base, index) => index).filter(index => bases[index] > 0);

  while (remaining > 0 && open.length > 0) {
    const share = Math.floor(remaining / open.length);

    if (share === 0) {
      for (const index of open) {
        if (remaining === 0) break;
        shares[index] += 1;
        remaining -= 1;
      }
      break;
    }

    for (const index of open) {
      const taken = Math.min(share, bases[index] - shares[index]);
      shares[index] += taken;
      remaining -= taken;
    }

    open = open.filter(index => shares[index] < bases[index]);
  }

  return shares;
}

const percentOffDiscount = (base: number, percentOff: number) =>
  Math.min(base, Math.round(base - base * (1 - percentOff / 100)));

/** The discount each line takes: percentOff line by line, amountOff once across them all. */
export function getCouponLineDiscounts(
  coupon: CouponAmounts | null | undefined,
  bases: readonly number[],
): number[] {
  if (!coupon) return bases.map(() => 0);
  if (coupon.percentOff) {
    const percentOff = coupon.percentOff;
    return bases.map(base => (base > 0 ? percentOffDiscount(base, percentOff) : 0));
  }
  if (coupon.amountOff) return splitAmountOffEqually(coupon.amountOff, bases);

  return bases.map(() => 0);
}

export type CouponBumpLine = { productId: CouponScopeId; base: number };

/** All the tickets of one ticket type together. */
export interface CouponTicketLine {
  ticketTypeId: CouponScopeId;
  base: number;
  /** Absent on lines built only from tickets bought. */
  quantity?: number;
}

/** A ticket line's base: its tickets after their bulk tier, plus their fees when the coupon covers them. */
export const getCouponTicketLineBase = ({
  unitPrice,
  quantity,
  feeAmount = 0,
  appliesToTicketFees = false,
}: {
  unitPrice: number;
  quantity: number;
  feeAmount?: number;
  appliesToTicketFees?: boolean | null;
}): number =>
  Math.max(0, Math.round(unitPrice * quantity)) +
  (appliesToTicketFees ? Math.max(0, feeAmount) * quantity : 0);

export interface CouponAllocation {
  primaryScope: CouponPrimaryScope;
  /** False when no rule matched: the code is accepted and takes nothing. */
  reachesOrder: boolean;
  reach: CouponReach;
  /** The order-level discount on the main product or the tickets; per-ticket discounts are priced apart. */
  primaryDiscount: number;
  /** Each ticket line's share of an amount off that sharesTicketAmountOff, in `ticketLines` order. */
  ticketDiscounts?: number[];
  bumpDiscounts: number[];
  bumpReached: boolean[];
}

/** A ticketsAndAddOns amount off comes off once per order, shared with the add-ons. */
export const sharesTicketAmountOff = (
  coupon: CouponAmounts | null | undefined,
  reach: Pick<CouponReach, 'ticketsShareWithAddOns'>,
): boolean => Boolean(coupon?.amountOff) && reach.ticketsShareWithAddOns;

const isTicketLineBought = (line: CouponTicketLine) => (line.quantity ?? 1) > 0;

/**
 * Which lines a coupon discounts on one purchase, and by how much: the main line or the tickets
 * and the bumps its matching rules reach.
 */
export function allocateCouponDiscounts(
  coupon: (CouponScope & CouponAmounts) | null | undefined,
  {
    pageId,
    pageTicketTypeIds,
    primary,
    primaryBase,
    bumps,
    ticketLines,
    amountOffStaysOnPrimary = false,
  }: {
    pageId: CouponScopeId;
    pageTicketTypeIds?: IdList;
    primary?: (CouponPriceLine & { base: number }) | null;
    /** The main line's base when it has no product: an event's tickets. */
    primaryBase?: number;
    bumps: readonly CouponBumpLine[];
    ticketLines?: readonly CouponTicketLine[];
    /** A subscription's amount off recurs on its main line, so bumps share none of it while that line takes it. */
    amountOffStaysOnPrimary?: boolean;
  },
): CouponAllocation {
  const reach = resolveCouponReach(coupon, {
    pageId,
    pageTicketTypeIds,
    priceId: primary?.priceId,
    ticketTypeIds: (ticketLines ?? []).filter(isTicketLineBought).map(line => line.ticketTypeId),
    bumpProductIds: bumps.map(bump => bump.productId),
  });
  const { primaryScope } = reach;
  const bumpReached = bumps.map(bump => couponReachesBump(reach, bump.productId));

  if (sharesTicketAmountOff(coupon, reach)) {
    return {
      primaryScope,
      reachesOrder: reach.applied,
      reach,
      primaryDiscount: 0,
      ...shareTicketAmountOff(coupon!, reach, { ticketLines: ticketLines ?? [], bumps }),
      bumpReached,
    };
  }

  const primaryInSplit = primaryScope === 'order';
  const bumpsShareAmountOff = !(amountOffStaysOnPrimary && primaryInSplit && coupon?.amountOff);
  const lines = [
    ...(primaryInSplit
      ? [{ kind: 'primary' as const, base: primary?.base ?? primaryBase ?? 0 }]
      : []),
    ...bumps.flatMap((bump, index) =>
      bumpReached[index] && (bumpsShareAmountOff || coupon?.percentOff)
        ? [{ kind: index, base: bump.base }]
        : [],
    ),
  ];

  const discounts = getCouponLineDiscounts(
    coupon,
    lines.map(line => line.base),
  );
  const bumpDiscounts = bumps.map(() => 0);
  let primaryDiscount = 0;

  lines.forEach((line, index) => {
    if (line.kind === 'primary') primaryDiscount = discounts[index];
    else bumpDiscounts[line.kind] = discounts[index];
  });

  return {
    primaryScope,
    reachesOrder: reach.applied,
    reach,
    primaryDiscount,
    ...(ticketLines && { ticketDiscounts: ticketLines.map(() => 0) }),
    bumpDiscounts,
    bumpReached,
  };
}

/**
 * The coupon as tickets are priced one by one: limited to the ticket types its matching rules
 * reach, or the coupon itself when they do not reach tickets one by one.
 */
export function getTicketCoupon<T extends CouponAmounts & { appliesToTicketFees?: boolean | null }>(
  coupon: T | null | undefined,
  reach: Pick<CouponReach, 'primaryScope' | 'ticketTypes'>,
):
  | T
  | (CouponAmounts & { appliesToTicketFees?: boolean | null; ticketTypes: string[] })
  | null
  | undefined {
  if (!coupon || reach.primaryScope !== 'ticket') return coupon;
  return {
    amountOff: coupon.amountOff,
    percentOff: coupon.percentOff,
    appliesToTicketFees: coupon.appliesToTicketFees,
    ticketTypes: reach.ticketTypes,
  };
}

/**
 * An amount off that sharesTicketAmountOff, split equally across each reached ticket line and
 * each reached add-on. Odd cents go to the first ticket line.
 */
export function shareTicketAmountOff(
  coupon: CouponAmounts,
  reach: Pick<CouponReach, 'applied' | 'ticketTypes' | 'allBumps' | 'bumps'>,
  {
    ticketLines,
    bumps,
  }: {
    ticketLines: readonly CouponTicketLine[];
    bumps: readonly CouponBumpLine[];
  },
): { ticketDiscounts: number[]; bumpDiscounts: number[] } {
  const shares = splitAmountOffEqually(coupon.amountOff ?? 0, [
    ...ticketLines.map(line => (includesId(reach.ticketTypes, line.ticketTypeId) ? line.base : 0)),
    ...bumps.map(bump => (couponReachesBump(reach, bump.productId) ? bump.base : 0)),
  ]);

  return {
    ticketDiscounts: shares.slice(0, ticketLines.length),
    bumpDiscounts: shares.slice(ticketLines.length),
  };
}

export interface TicketLineShareGroup {
  quantity: number;
  /** Per ticket, in whole minor units. */
  price: number;
  priceShare: number;
  feeShare: number;
}

const spreadWhole = (total: number, quantity: number, index: number) =>
  Math.floor(total / quantity) + (index < total % quantity ? 1 : 0);

/**
 * A ticket line's share of an amount off, spread over its tickets in whole cents with the odd cents
 * on the first tickets. Each ticket's share takes its price first and its fee after. Tickets that
 * come out the same are grouped, in ticket order.
 */
export function spreadTicketLineShare({
  priceTotal,
  quantity,
  share,
}: {
  /** The line's tickets after their bulk tier, in whole minor units. */
  priceTotal: number;
  quantity: number;
  share: number;
}): TicketLineShareGroup[] {
  const groups: TicketLineShareGroup[] = [];

  for (let index = 0; index < quantity; index += 1) {
    const ticketPrice = spreadWhole(priceTotal, quantity, index);
    const ticketShare = spreadWhole(share, quantity, index);
    const priceShare = Math.min(ticketShare, ticketPrice);
    const ticket = {
      price: ticketPrice - priceShare,
      priceShare,
      feeShare: ticketShare - priceShare,
    };
    const last = groups[groups.length - 1];

    if (
      last &&
      last.price === ticket.price &&
      last.priceShare === ticket.priceShare &&
      last.feeShare === ticket.feeShare
    ) {
      last.quantity += 1;
    } else {
      groups.push({ quantity: 1, ...ticket });
    }
  }

  return groups;
}
